import { IconButton } from '@atlaskit/button/new';
import VideoPauseIcon from '@atlaskit/icon/core/video-pause';
import VideoPlayIcon from '@atlaskit/icon/core/video-play';
import Image from '@atlaskit/image';
import { Box, Inline, Pressable, Text, xcss } from '@atlaskit/primitives';
import { SpotlightActions } from '@atlaskit/spotlight/actions';
import { SpotlightBody } from '@atlaskit/spotlight/body';
import { SpotlightCard } from '@atlaskit/spotlight/card';
import { SpotlightControls } from '@atlaskit/spotlight/controls';
import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';
import { SpotlightFooter } from '@atlaskit/spotlight/footer';
import { SpotlightHeader } from '@atlaskit/spotlight/header';
import { SpotlightHeadline } from '@atlaskit/spotlight/headline';
import { SpotlightMedia } from '@atlaskit/spotlight/media';
import { PopoverContent } from '@atlaskit/spotlight/popover-content';
import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';
import { PopoverTarget } from '@atlaskit/spotlight/popover-target';
import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';
import Tooltip, { type PositionType } from '@atlaskit/tooltip';
import { AiSummaryIcon } from '@databases/icons/AiSummaryIcon';
import { FieldTypeIcon } from '@databases/icons/FieldTypeIcon';
import { DatabaseFieldType } from '@databases/types/database';
import React, {
  type FunctionComponent,
  type MutableRefObject,
  type Ref,
  useEffect,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl-next';

import {
  ChangeboardingExperience,
  useIsChangeboardingActive
} from '@canvas/changeboarding';
import { useMediaQuery } from '@canvas/hooks';

import aiSummarySpotlightImage from './assets/ai-summary-changeboarding.svg';
import aiSummarySpotlightStaticImage from './assets/ai-summary-field-static.png';
import { addFieldMenuMessages } from './messages.ts';
import type { AddFieldMenuSelection, FieldTypeEntry } from './types';

const itemStyles = xcss({
  width: 'calc(100% + 8px)',
  marginInlineStart: 'space.negative.050',
  borderRadius: 'radius.small',
  paddingInlineStart: 'space.100',
  paddingInlineEnd: 'space.100',
  paddingBlock: 'space.075',
  color: 'color.text',
  backgroundColor: 'color.background.neutral.subtle',
  ':hover': {
    backgroundColor: 'color.background.neutral.subtle.hovered'
  },
  ':active': {
    backgroundColor: 'color.background.neutral.subtle.pressed'
  }
});

const iconWrapperStyles = xcss({
  display: 'inline-flex',
  alignItems: 'center'
});

const spotlightMediaContainerStyles = xcss({
  position: 'relative'
});

const animationControlContainerStyles = xcss({
  position: 'absolute',
  right: 'space.100',
  bottom: 'space.100'
});

const tooltipPositions: Partial<Record<DatabaseFieldType, PositionType>> = {
  [DatabaseFieldType.HYPERLINK]: 'top',
  [DatabaseFieldType.USER]: 'top',
  [DatabaseFieldType.ATTACHMENT]: 'bottom',
  [DatabaseFieldType.CHECKBOX]: 'bottom',
  [DatabaseFieldType.ENTRY_LINK]: 'top',
  [DatabaseFieldType.BACKLINK]: 'top',
  [DatabaseFieldType.ENTRY_DETAILS]: 'bottom',
  [DatabaseFieldType.JIRA_ISSUE_DETAILS]: 'top'
};

export const FieldTypeMenuItem: FunctionComponent<{
  fieldType: FieldTypeEntry;
  onSelect?: (selection: AddFieldMenuSelection) => void;
}> = ({ fieldType, onSelect }) => {
  const Icon = fieldType.Icon;
  const tooltipPosition = tooltipPositions[fieldType.type] ?? 'left';

  const menuItem = (
    <Pressable
      xcss={itemStyles}
      onClick={() => onSelect?.({ type: 'field-type', fieldType: fieldType.type })}
      testId={`add-field-menu-${fieldType.type}`}
    >
      <Inline space="space.100" alignBlock="center" shouldWrap={false}>
        <Box xcss={iconWrapperStyles}>
          {Icon ? (
            <Icon label="" />
          ) : (
            <FieldTypeIcon
              type={fieldType.type}
              label={fieldType.labelDescriptor}
              noTooltip
            />
          )}
        </Box>
        <Text>{fieldType.label}</Text>
      </Inline>
    </Pressable>
  );

  return fieldType.tooltip ? (
    <Tooltip
      content={fieldType.tooltip}
      hideTooltipOnMouseDown
      position={tooltipPosition}
    >
      {menuItem}
    </Tooltip>
  ) : (
    menuItem
  );
};

const ChangeboardingPrimaryActionRegistration: FunctionComponent<{
  actionRef: MutableRefObject<(() => void) | undefined>;
  onPrimaryAction: () => void;
}> = ({ actionRef, onPrimaryAction }) => {
  useEffect(() => {
    actionRef.current = onPrimaryAction;

    return () => {
      if (actionRef.current === onPrimaryAction) {
        actionRef.current = undefined;
      }
    };
  }, [actionRef, onPrimaryAction]);

  return null;
};

export const AiSummarizeMenuItem: FunctionComponent<{
  spotlightRef?: Ref<HTMLDivElement>;
  onSelect?: (selection: AddFieldMenuSelection) => void;
  onChangeboardingShown?: () => void;
}> = ({ spotlightRef, onSelect, onChangeboardingShown }) => {
  const { formatMessage } = useIntl();
  const changeboardingPrimaryActionRef = useRef<() => void>();
  const hasFiredChangeboardingPrimaryActionRef = useRef(false);
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [isAnimationPaused, setIsAnimationPaused] = useState(prefersReducedMotion);
  const isChangeboardingActive = useIsChangeboardingActive('dbAiSummary');

  useEffect(() => {
    if (prefersReducedMotion) {
      setIsAnimationPaused(true);
    }
  }, [prefersReducedMotion]);
  const handleSelect = () =>
    onSelect?.({
      type: 'ai-summarize',
      fieldType: DatabaseFieldType.AI_SUMMARY
    });

  const handleMenuItemSelect = () => {
    handleSelect();

    if (hasFiredChangeboardingPrimaryActionRef.current) {
      return;
    }

    const onPrimaryAction = changeboardingPrimaryActionRef.current;
    changeboardingPrimaryActionRef.current = undefined;
    if (onPrimaryAction) {
      hasFiredChangeboardingPrimaryActionRef.current = true;
      globalThis.queueMicrotask(onPrimaryAction);
    }
  };

  const renderMenuItem = (onClick: () => void) => (
    <Pressable xcss={itemStyles} onClick={onClick} testId="add-field-menu-ai-summary">
      <Inline space="space.100" alignBlock="center" shouldWrap={false}>
        <Box xcss={iconWrapperStyles}>
          <AiSummaryIcon label="" />
        </Box>
        <Text>{formatMessage(addFieldMenuMessages.aiSummary)}</Text>
      </Inline>
    </Pressable>
  );

  const menuItem = (
    <Tooltip
      content={formatMessage(addFieldMenuMessages.aiSummaryTooltip)}
      hideTooltipOnMouseDown
      position={isChangeboardingActive ? 'top' : 'left'}
    >
      {renderMenuItem(handleMenuItemSelect)}
    </Tooltip>
  );
  const portalContainer = typeof document !== 'undefined' ? document.body : null;

  if (!portalContainer || !isChangeboardingActive) {
    return menuItem;
  }

  return (
    <PopoverProvider>
      <PopoverTarget>
        <Box>{menuItem}</Box>
      </PopoverTarget>
      <ChangeboardingExperience
        feature="dbAiSummary"
        defaultRender={null}
        onMount={onChangeboardingShown}
      >
        {({ onDismiss, onPrimaryAction }) => (
          <>
            {/* The menu item is outside this render prop, so register the action its shared click handler invokes. */}
            <ChangeboardingPrimaryActionRegistration
              actionRef={changeboardingPrimaryActionRef}
              onPrimaryAction={onPrimaryAction}
            />
            {createPortal(
              <PopoverContent
                placement="left-end"
                offset={[0, 24]}
                strategy="fixed"
                dismiss={onDismiss}
                done={onDismiss}
                testId="ai-summary-onboarding-spotlight"
              >
                <SpotlightCard ref={spotlightRef}>
                  <SpotlightHeader>
                    <SpotlightHeadline>
                      {formatMessage(addFieldMenuMessages.aiSummarySpotlightTitle)}
                    </SpotlightHeadline>
                    <SpotlightControls>
                      <SpotlightDismissControl onClick={onDismiss} />
                    </SpotlightControls>
                  </SpotlightHeader>
                  <SpotlightMedia>
                    <Box xcss={spotlightMediaContainerStyles}>
                      <Image
                        src={
                          isAnimationPaused
                            ? aiSummarySpotlightStaticImage
                            : aiSummarySpotlightImage
                        }
                        alt={formatMessage(addFieldMenuMessages.aiSummarySpotlightTitle)}
                        width="100%"
                      />
                      <Box xcss={animationControlContainerStyles}>
                        <IconButton
                          appearance="default"
                          spacing="compact"
                          icon={isAnimationPaused ? VideoPlayIcon : VideoPauseIcon}
                          label={formatMessage(
                            isAnimationPaused
                              ? addFieldMenuMessages.aiSummarySpotlightPlayAnimation
                              : addFieldMenuMessages.aiSummarySpotlightPauseAnimation
                          )}
                          isTooltipDisabled={false}
                          tooltip={{ hideTooltipOnClick: true }}
                          onClick={() => setIsAnimationPaused((isPaused) => !isPaused)}
                          testId="ai-summary-spotlight-animation-control"
                        />
                      </Box>
                    </Box>
                  </SpotlightMedia>
                  <SpotlightBody>
                    {formatMessage(addFieldMenuMessages.aiSummarySpotlightBody)}
                  </SpotlightBody>
                  <SpotlightFooter>
                    <SpotlightActions>
                      <SpotlightPrimaryAction onClick={handleMenuItemSelect}>
                        {formatMessage(addFieldMenuMessages.aiSummarySpotlightTryItNow)}
                      </SpotlightPrimaryAction>
                    </SpotlightActions>
                  </SpotlightFooter>
                </SpotlightCard>
              </PopoverContent>,
              portalContainer
            )}
          </>
        )}
      </ChangeboardingExperience>
    </PopoverProvider>
  );
};
